import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, formatErr, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { ItemCombobox } from "@/components/ItemCombobox";
import { PaginationBar, usePagination } from "@/components/Pagination";
import { Layers, Plus, Save, X, Pencil, Trash2, FilterX, Upload, Download, FileSpreadsheet, FileDown, CheckCircle2, AlertCircle, Hash } from "lucide-react";
import { toast } from "sonner";

const emptyLine = () => ({
  item_id: "", batch_number: "", store_id: "", qty: 1, unit: "pcs", mrp: 0, purchase_rate: 0,
  serial_numbers: [],
});
const emptyForm = () => ({
  entry_date: new Date().toISOString().slice(0, 10),
  remarks: "", lines: [emptyLine()],
});
const emptyFilters = () => ({
  item: "", category: "", company: "", group: "", batch_number: "",
  store_id: "", date_from: "", date_to: "",
});
const token = () => localStorage.getItem("erp_token");

function SerialDialog({ itemName, initial, onClose, onApply }) {
  const [serials, setSerials] = useState(initial.length ? [...initial] : [""]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const fileRef = useRef(null);

  const dupSet = useMemo(() => {
    const seen = new Set(); const dups = new Set();
    serials.forEach(s => { const v = s.trim(); if (!v) return; if (seen.has(v)) dups.add(v); else seen.add(v); });
    return dups;
  }, [serials]);
  const validCount = useMemo(() => new Set(serials.map(s => s.trim()).filter(Boolean)).size, [serials]);

  const onFile = async (e) => {
    const f = e.target.files[0]; e.target.value = "";
    if (!f) return;
    setLoading(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const res = await fetch(`${API}/stocks/batch-opening/parse-serials`,
        { method: "POST", headers: { Authorization: `Bearer ${token()}` }, credentials: "include", body: fd });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Parse failed");
      const raw = (data.serials || []).map(s => String(s).trim());
      const invalid = raw.length - raw.filter(Boolean).length;
      const clean = raw.filter(Boolean);
      const seen = new Set(); const dups = [];
      clean.forEach(s => { if (seen.has(s)) dups.push(s); else seen.add(s); });
      setSerials(clean.length ? clean : [""]);
      setSummary({ imported: seen.size, duplicates: dups, invalid });
      if (seen.size) toast.success(`${seen.size} serial number(s) imported`);
    } catch (err) { toast.error(String(err.message || err)); }
    finally { setLoading(false); }
  };

  const setVal = (i, v) => setSerials(s => { const n = [...s]; n[i] = v; return n; });
  const addRow = () => setSerials(s => [...s, ""]);
  const delRow = (i) => setSerials(s => s.filter((_, x) => x !== i));
  const apply = () => onApply([...new Set(serials.map(s => s.trim()).filter(Boolean))]);

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="rounded-sm bg-card border-border max-w-lg max-h-[90vh] overflow-y-auto" data-testid="bwso-serial-dialog" aria-describedby="bwso-serial-desc">
        <DialogHeader><DialogTitle className="font-display uppercase">Serial Numbers — {itemName || "Item"}</DialogTitle></DialogHeader>
        <p id="bwso-serial-desc" className="sr-only">Enter or import serial numbers for this item line; quantity syncs to the valid unique serial count.</p>
        <div className="space-y-3">
          <div className="border border-primary/30 bg-primary/5 rounded-sm p-3 text-xs flex items-center justify-between gap-2">
            <span>Upload a CSV/Excel file with one serial number per row (first column).</span>
            <Button data-testid="bwso-serial-import-btn" size="sm" variant="outline" onClick={() => fileRef.current?.click()} disabled={loading} className="rounded-sm text-xs uppercase tracking-wider shrink-0">
              <Upload className="w-3 h-3 mr-1"/> {loading ? "Reading…" : "Import Serial No"}
            </Button>
            <input ref={fileRef} data-testid="bwso-serial-file" type="file" accept=".csv,.xlsx" onChange={onFile} className="hidden"/>
          </div>
          {summary && (
            <div className="border border-border rounded-sm p-3 text-xs space-y-1" data-testid="bwso-serial-summary">
              <div className="flex items-center gap-2 text-green-500"><CheckCircle2 className="w-3.5 h-3.5"/> <span className="font-mono font-semibold" data-testid="bwso-serial-imported">{summary.imported}</span> serial number(s) imported</div>
              {summary.duplicates.length > 0 && (
                <div className="flex items-start gap-2 text-yellow-500" data-testid="bwso-serial-duplicates"><AlertCircle className="w-3.5 h-3.5 mt-0.5"/> <span>{summary.duplicates.length} duplicate(s) highlighted: <span className="font-mono">{summary.duplicates.slice(0, 10).join(", ")}{summary.duplicates.length > 10 ? "…" : ""}</span></span></div>
              )}
              {summary.invalid > 0 && (
                <div className="flex items-center gap-2 text-destructive" data-testid="bwso-serial-invalid"><AlertCircle className="w-3.5 h-3.5"/> {summary.invalid} empty/invalid entr{summary.invalid === 1 ? "y" : "ies"} ignored</div>
              )}
            </div>
          )}
          <div className="space-y-2 max-h-64 overflow-y-auto" data-testid="bwso-serial-list">
            {serials.map((s, i) => (
              <div key={i} className="flex items-center gap-2">
                <Input data-testid={`bwso-serial-input-${i}`} value={s} onChange={(e) => setVal(i, e.target.value)} placeholder={`Serial #${i + 1}`}
                  className={`rounded-sm bg-secondary h-8 text-xs font-mono ${s.trim() && dupSet.has(s.trim()) ? "border-yellow-500/70 text-yellow-500" : "border-border"}`}/>
                <Button type="button" data-testid={`bwso-serial-del-${i}`} variant="ghost" size="sm" onClick={() => delRow(i)} disabled={serials.length === 1} className="h-7 w-7 p-0 text-destructive shrink-0"><Trash2 className="w-3 h-3"/></Button>
              </div>
            ))}
          </div>
          <div className="flex items-center justify-between">
            <Button type="button" data-testid="bwso-serial-add" size="sm" variant="outline" onClick={addRow} className="rounded-sm text-xs uppercase tracking-wider"><Plus className="w-3 h-3 mr-1"/> Add Serial</Button>
            <span className="text-xs text-muted-foreground font-mono" data-testid="bwso-serial-valid-count">{validCount} valid</span>
          </div>
          <div className="flex gap-2 justify-end border-t border-border pt-3">
            <Button data-testid="bwso-serial-done" onClick={apply} className="rounded-sm text-xs uppercase tracking-wider"><CheckCircle2 className="w-3 h-3 mr-1"/> Done — Set Qty {validCount || ""}</Button>
            <Button data-testid="bwso-serial-cancel" variant="outline" onClick={onClose} className="rounded-sm text-xs uppercase tracking-wider">Cancel</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function BatchWiseStockOpening() {
  const [mode, setMode] = useState("list"); // "list" | "form"
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({ count: 0, total_qty: 0, total_value: 0 });
  const [items, setItems] = useState([]);
  const [stores, setStores] = useState([]);
  const [saving, setSaving] = useState(false);
  const [delRow, setDelRow] = useState(null);
  const [filters, setFilters] = useState(emptyFilters());
  const [importOpen, setImportOpen] = useState(false);
  const [serialIdx, setSerialIdx] = useState(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [importing, setImporting] = useState(false);
  const pg = usePagination(rows);

  const load = useCallback(async () => {
    try {
      const p = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) p.set(k, v); });
      const [bo, it, st] = await Promise.all([
        api.get(`/stocks/batch-opening?${p.toString()}`), api.get("/items?scope=all"), api.get("/stores"),
      ]);
      setRows(bo.data.rows || []);
      setTotals({ count: bo.data.count || 0, total_qty: bo.data.total_qty || 0, total_value: bo.data.total_value || 0 });
      setItems(Array.isArray(it.data) ? it.data : (it.data.items || []));
      setStores(st.data || []);
    } catch (e) { toast.error(formatErr(e)); }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const otherStores = useMemo(() => stores.filter(s => s.store_type !== "Godown"), [stores]);
  const catOpts = useMemo(() => [...new Set(items.map(i => i.category).filter(Boolean))].sort(), [items]);
  const compOpts = useMemo(() => [...new Set(items.map(i => i.company).filter(Boolean))].sort(), [items]);
  const grpOpts = useMemo(() => [...new Set(items.map(i => i.group).filter(Boolean))].sort(), [items]);
  const filtersActive = useMemo(() => Object.values(filters).some(v => v), [filters]);
  const linesTotal = useMemo(() => form.lines.reduce((a, l) => a + (Number(l.qty) || 0) * (Number(l.purchase_rate) || 0), 0), [form.lines]);
  const linesQty = useMemo(() => form.lines.reduce((a, l) => a + (Number(l.qty) || 0), 0), [form.lines]);

  const openNew = () => { setEditing(null); setForm(emptyForm()); setMode("form"); };
  const openEdit = (r) => {
    setEditing(r.id);
    setForm({
      entry_date: r.entry_date || new Date().toISOString().slice(0, 10),
      remarks: r.remarks || "",
      lines: [{
        item_id: r.item_id || "", batch_number: r.batch_number || "",
        store_id: r.store_id || "", qty: r.qty, unit: r.unit || "pcs",
        mrp: r.mrp || 0, purchase_rate: r.purchase_rate || 0,
        serial_numbers: r.serial_numbers || [],
      }],
    });
    setMode("form");
  };
  const cancel = () => { setMode("list"); setEditing(null); setForm(emptyForm()); };

  const setLine = (i, k, v) => setForm(f => {
    const lines = [...f.lines];
    lines[i] = { ...lines[i], [k]: v };
    return { ...f, lines };
  });
  const pickItem = (i, itm) => setForm(f => {
    const lines = [...f.lines];
    lines[i] = { ...lines[i], item_id: itm.id, unit: itm.unit || "pcs",
      mrp: Number(itm.mrp || 0), purchase_rate: Number(itm.per_unit_cost || 0) };
    return { ...f, lines };
  });
  const addLine = () => setForm(f => ({ ...f, lines: [...f.lines, emptyLine()] }));
  const delLine = (i) => setForm(f => ({ ...f, lines: f.lines.filter((_, x) => x !== i) }));
  const applySerials = (i, serials) => setForm(f => {
    const lines = [...f.lines];
    lines[i] = { ...lines[i], serial_numbers: serials, ...(serials.length ? { qty: serials.length } : {}) };
    return { ...f, lines };
  });

  const payloadOf = (l) => ({
    entry_date: form.entry_date || null,
    item_id: l.item_id,
    batch_number: l.batch_number.trim(),
    store_id: l.store_id || null,
    qty: Number(l.qty),
    mrp: Number(l.mrp) || 0,
    purchase_rate: Number(l.purchase_rate) || 0,
    serial_numbers: (l.serial_numbers || []).map(s => String(s).trim()).filter(Boolean),
    remarks: form.remarks.trim() || null,
  });

  const save = async () => {
    const badIdx = form.lines.findIndex(l => !l.item_id || !l.batch_number.trim() || !(Number(l.qty) > 0));
    if (badIdx !== -1) return toast.error(`Line ${badIdx + 1}: item, batch number and quantity > 0 are required`);
    setSaving(true);
    try {
      if (editing) {
        await api.patch(`/stocks/batch-opening/${editing}`, payloadOf(form.lines[0]));
        toast.success("Opening entry updated");
        cancel();
        await load();
      } else {
        const failed = [];
        for (let i = 0; i < form.lines.length; i++) {
          try { await api.post("/stocks/batch-opening", payloadOf(form.lines[i])); }
          catch (e) { failed.push({ line: form.lines[i], n: i + 1, err: formatErr(e) }); }
        }
        const okCount = form.lines.length - failed.length;
        if (okCount > 0) toast.success(`${okCount} opening ${okCount === 1 ? "entry" : "entries"} saved`);
        if (failed.length === 0) {
          cancel();
        } else {
          toast.error(`Line(s) ${failed.map(x => x.n).join(", ")} failed — ${failed[0].err}`);
          setForm(f => ({ ...f, lines: failed.map(x => x.line) }));
        }
        await load();
      }
    } catch (e) { toast.error(formatErr(e)); }
    finally { setSaving(false); }
  };

  const doDelete = async () => {
    if (!delRow) return;
    try {
      await api.delete(`/stocks/batch-opening/${delRow.id}`);
      toast.success("Opening entry deleted");
      setDelRow(null);
      await load();
    } catch (e) { toast.error(formatErr(e)); setDelRow(null); }
  };

  const exportQs = () => {
    const p = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v) p.set(k, v); });
    const qs = p.toString();
    return qs ? `?${qs}` : "";
  };
  const download = async (url, filename) => {
    try {
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token()}` }, credentials: "include" });
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob); a.download = filename; a.click();
    } catch (e) { toast.error(formatErr(e)); }
  };
  const exportXlsx = () => download(`${API}/stocks/batch-opening/export.xlsx${exportQs()}`, "batch_wise_stock_opening.xlsx");
  const exportPdf = () => download(`${API}/stocks/batch-opening/export.pdf${exportQs()}`, "batch_wise_stock_opening.pdf");
  const getTemplate = () => download(`${API}/stocks/batch-opening/template.csv`, "batch_opening_template.csv");

  const doPreview = async () => {
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const res = await fetch(`${API}/stocks/batch-opening/import?dry_run=true`,
        { method: "POST", headers: { Authorization: `Bearer ${token()}` }, credentials: "include", body: fd });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Preview failed");
      setPreview(data);
    } catch (e) { toast.error(String(e.message || e)); }
    finally { setImporting(false); }
  };

  const doImport = async () => {
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const res = await fetch(`${API}/stocks/batch-opening/import`,
        { method: "POST", headers: { Authorization: `Bearer ${token()}` }, credentials: "include", body: fd });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Import failed");
      toast.success(`${data.inserted || 0} imported${data.errors?.length ? ` · ${data.errors.length} skipped` : ""}`);
      if (data.errors?.length) console.warn("Import errors:", data.errors);
      setImportOpen(false); setFile(null); setPreview(null);
      await load();
    } catch (e) { toast.error(String(e.message || e)); }
    finally { setImporting(false); }
  };

  const selOpts = (opts) => opts.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>);

  return (
    <div className="p-8">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="text-primary text-xs uppercase tracking-[0.3em] mb-2 flex items-center gap-2"><Layers className="w-3 h-3"/> Stocks · Stock Data</div>
          <h1 className="font-display font-black text-4xl uppercase tracking-tighter">Batch Wise Stock Opening</h1>
          <div className="text-xs text-muted-foreground mt-1">Manually open batch-wise stock with store, rates and MRP details</div>
        </div>
        {mode === "list" && (
          <div className="flex items-center gap-2">
            <Button data-testid="bwso-import-btn" variant="outline" onClick={() => setImportOpen(true)} className="rounded-sm text-xs uppercase tracking-wider"><Upload className="w-3 h-3 mr-1"/> Import</Button>
            <div className="flex border border-border rounded-sm overflow-hidden">
              <button data-testid="bwso-export-xlsx" onClick={exportXlsx} className="px-3 h-9 text-xs uppercase tracking-wider flex items-center gap-1 text-muted-foreground hover:text-foreground hover:bg-secondary" title="Export Excel"><FileSpreadsheet className="w-3 h-3"/> Excel</button>
              <button data-testid="bwso-export-pdf" onClick={exportPdf} className="px-3 h-9 text-xs uppercase tracking-wider flex items-center gap-1 text-muted-foreground hover:text-foreground hover:bg-secondary border-l border-border" title="Export PDF"><FileDown className="w-3 h-3"/> PDF</button>
            </div>
            <Button data-testid="bwso-new-btn" onClick={openNew} className="rounded-sm text-xs uppercase tracking-wider"><Plus className="w-3 h-3 mr-1"/> New Opening Entry</Button>
          </div>
        )}
      </div>

      {mode === "form" && (
        <div className="border border-border bg-card rounded-sm p-4 mb-6" data-testid="bwso-form">
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-3">{editing ? "Edit Opening Entry" : "New Opening Entry"}</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <div>
              <Label className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Date</Label>
              <Input data-testid="bwso-date" type="date" value={form.entry_date} onChange={(e) => setForm(f => ({ ...f, entry_date: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border h-9"/>
            </div>
            <div className="col-span-2">
              <Label className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Remarks</Label>
              <Input data-testid="bwso-remarks" value={form.remarks} onChange={(e) => setForm(f => ({ ...f, remarks: e.target.value }))} placeholder="Optional note (applies to all lines)" className="mt-1 rounded-sm bg-secondary border-border h-9"/>
            </div>
          </div>

          <div className="overflow-x-auto border border-border rounded-sm">
            <table className="w-full text-xs" data-testid="bwso-lines-table">
              <thead className="bg-secondary text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
                <tr>
                  <th className="text-center p-2 min-w-[320px]">Item Name *</th>
                  <th className="text-center p-2 min-w-[90px]">Category</th>
                  <th className="text-center p-2 min-w-[90px]">Group</th>
                  <th className="text-center p-2 min-w-[80px]">Batch *</th>
                  <th className="text-center p-2 min-w-[80px]">Serial</th>
                  <th className="text-center p-2 min-w-[120px]">Store</th>
                  <th className="text-center p-2 min-w-[56px]">Qty *</th>
                  <th className="text-center p-2 min-w-[60px]">Unit</th>
                  <th className="text-center p-2 min-w-[70px]">MRP</th>
                  <th className="text-center p-2 min-w-[70px]">Purchase Rate</th>
                  <th className="text-center p-2 min-w-[90px]">Total Value</th>
                  <th className="p-2 w-10"></th>
                </tr>
              </thead>
              <tbody>
                {form.lines.map((l, i) => {
                  const itm = items.find(x => x.id === l.item_id);
                  const lineTotal = (Number(l.qty) || 0) * (Number(l.purchase_rate) || 0);
                  return (
                    <tr key={i} data-testid={`bwso-line-${i}`} className="border-t border-border align-middle">
                      <td className="p-1.5">
                        <ItemCombobox items={items} value={l.item_id} onSelect={(x) => pickItem(i, x)} placeholder="Pick item" testId={`bwso-line-item-${i}`} labelFn={(x) => x.item_name} triggerClass="h-8"/>
                      </td>
                      <td className="p-1.5 text-muted-foreground" data-testid={`bwso-line-category-${i}`}>{itm?.category || "—"}</td>
                      <td className="p-1.5 text-muted-foreground" data-testid={`bwso-line-group-${i}`}>{itm?.group || "—"}</td>
                      <td className="p-1.5">
                        <Input data-testid={`bwso-line-batch-${i}`} value={l.batch_number} onChange={(e) => setLine(i, "batch_number", e.target.value)} placeholder="B-001" className="rounded-sm bg-secondary border-border h-8 text-xs"/>
                      </td>
                      <td className="p-1.5 text-center">
                        <Button type="button" data-testid={`bwso-line-serials-${i}`} variant="outline" size="sm" onClick={() => setSerialIdx(i)}
                          className={`h-8 px-2 text-[10px] uppercase tracking-wider ${l.serial_numbers.length ? "border-green-500/50 text-green-500" : ""}`}>
                          <Hash className="w-3 h-3 mr-1"/>{l.serial_numbers.length ? l.serial_numbers.length : "Serial"}
                        </Button>
                      </td>
                      <td className="p-1.5">
                        <Select value={l.store_id || "none"} onValueChange={(v) => setLine(i, "store_id", v === "none" ? "" : v)}>
                          <SelectTrigger data-testid={`bwso-line-store-${i}`} className="rounded-sm bg-secondary border-border h-8 text-xs"><SelectValue/></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">— None —</SelectItem>
                            {otherStores.map(s => <SelectItem key={s.id} value={s.id} data-testid={`bwso-line-store-opt-${i}-${s.id}`}>{s.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="p-1.5">
                        <Input data-testid={`bwso-line-qty-${i}`} type="number" min="0" step="any" value={l.qty} onChange={(e) => setLine(i, "qty", e.target.value)} className="rounded-sm bg-secondary border-border h-8 text-xs text-right"/>
                      </td>
                      <td className="p-1.5 text-muted-foreground" data-testid={`bwso-line-unit-${i}`}>{l.unit || "—"}</td>
                      <td className="p-1.5">
                        <Input data-testid={`bwso-line-mrp-${i}`} type="number" min="0" step="any" value={l.mrp} onChange={(e) => setLine(i, "mrp", e.target.value)} className="rounded-sm bg-secondary border-border h-8 text-xs text-right"/>
                      </td>
                      <td className="p-1.5">
                        <Input data-testid={`bwso-line-rate-${i}`} type="number" min="0" step="any" value={l.purchase_rate} onChange={(e) => setLine(i, "purchase_rate", e.target.value)} className="rounded-sm bg-secondary border-border h-8 text-xs text-right"/>
                      </td>
                      <td className="p-1.5 text-right font-mono" data-testid={`bwso-line-total-${i}`}>₹{lineTotal.toLocaleString("en-IN", { maximumFractionDigits: 2 })}</td>
                      <td className="p-1.5 text-center">
                        <Button type="button" data-testid={`bwso-line-del-${i}`} variant="ghost" size="sm" onClick={() => delLine(i)} disabled={form.lines.length === 1} className="h-7 w-7 p-0 text-destructive"><Trash2 className="w-3 h-3"/></Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {!editing && (
            <Button type="button" data-testid="bwso-add-line-btn" size="sm" variant="outline" onClick={addLine} className="mt-2 rounded-sm text-xs uppercase tracking-wider"><Plus className="w-3 h-3 mr-1"/> Add Item Line</Button>
          )}

          <div className="flex items-center justify-between mt-4">
            <div className="text-xs text-muted-foreground font-mono" data-testid="bwso-lines-summary">
              {form.lines.length} line(s) · Qty {linesQty} · Total ₹{linesTotal.toLocaleString("en-IN", { maximumFractionDigits: 2 })}
            </div>
            <div className="flex gap-2">
              <Button data-testid="bwso-save-btn" onClick={save} disabled={saving} className="rounded-sm text-xs uppercase tracking-wider"><Save className="w-3 h-3 mr-1"/> {saving ? "Saving…" : editing ? "Update Entry" : "Save Entries"}</Button>
              <Button data-testid="bwso-cancel-btn" variant="outline" onClick={cancel} className="rounded-sm text-xs uppercase tracking-wider"><X className="w-3 h-3 mr-1"/> Cancel</Button>
            </div>
          </div>
        </div>
      )}

      {mode === "list" && (
        <div className="flex flex-wrap items-end gap-2 mb-4 border border-border bg-card rounded-sm p-3" data-testid="bwso-filter-bar">
          <div className="min-w-[160px]">
            <Label className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Item Name</Label>
            <Input data-testid="bwso-filter-item" value={filters.item} onChange={(e) => setFilters(f => ({ ...f, item: e.target.value }))} placeholder="Search item…" className="mt-1 rounded-sm bg-secondary border-border h-8 text-xs"/>
          </div>
          <div className="w-[130px]">
            <Label className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Category</Label>
            <Select value={filters.category || "all"} onValueChange={(v) => setFilters(f => ({ ...f, category: v === "all" ? "" : v }))}>
              <SelectTrigger data-testid="bwso-filter-category" className="mt-1 rounded-sm bg-secondary border-border h-8 text-xs"><SelectValue/></SelectTrigger>
              <SelectContent><SelectItem value="all">All</SelectItem>{selOpts(catOpts)}</SelectContent>
            </Select>
          </div>
          <div className="w-[130px]">
            <Label className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Company</Label>
            <Select value={filters.company || "all"} onValueChange={(v) => setFilters(f => ({ ...f, company: v === "all" ? "" : v }))}>
              <SelectTrigger data-testid="bwso-filter-company" className="mt-1 rounded-sm bg-secondary border-border h-8 text-xs"><SelectValue/></SelectTrigger>
              <SelectContent><SelectItem value="all">All</SelectItem>{selOpts(compOpts)}</SelectContent>
            </Select>
          </div>
          <div className="w-[130px]">
            <Label className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Group</Label>
            <Select value={filters.group || "all"} onValueChange={(v) => setFilters(f => ({ ...f, group: v === "all" ? "" : v }))}>
              <SelectTrigger data-testid="bwso-filter-group" className="mt-1 rounded-sm bg-secondary border-border h-8 text-xs"><SelectValue/></SelectTrigger>
              <SelectContent><SelectItem value="all">All</SelectItem>{selOpts(grpOpts)}</SelectContent>
            </Select>
          </div>
          <div className="w-[120px]">
            <Label className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Batch Number</Label>
            <Input data-testid="bwso-filter-batch" value={filters.batch_number} onChange={(e) => setFilters(f => ({ ...f, batch_number: e.target.value }))} placeholder="Batch…" className="mt-1 rounded-sm bg-secondary border-border h-8 text-xs"/>
          </div>
          <div className="w-[130px]">
            <Label className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Store</Label>
            <Select value={filters.store_id || "all"} onValueChange={(v) => setFilters(f => ({ ...f, store_id: v === "all" ? "" : v }))}>
              <SelectTrigger data-testid="bwso-filter-store" className="mt-1 rounded-sm bg-secondary border-border h-8 text-xs"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                {otherStores.map(s => <SelectItem key={s.id} value={s.id} data-testid={`bwso-filter-store-opt-${s.id}`}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="w-[140px]">
            <Label className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">From Date</Label>
            <Input data-testid="bwso-filter-date-from" type="date" value={filters.date_from} onChange={(e) => setFilters(f => ({ ...f, date_from: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border h-8 text-xs"/>
          </div>
          <div className="w-[140px]">
            <Label className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">To Date</Label>
            <Input data-testid="bwso-filter-date-to" type="date" value={filters.date_to} onChange={(e) => setFilters(f => ({ ...f, date_to: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border h-8 text-xs"/>
          </div>
          {filtersActive && (
            <Button data-testid="bwso-filter-clear" size="sm" variant="outline" onClick={() => setFilters(emptyFilters())} className="rounded-sm text-xs uppercase tracking-wider"><FilterX className="w-3 h-3 mr-1"/> Clear</Button>
          )}
        </div>
      )}

      <div className="grid grid-cols-3 gap-3 mb-3 text-xs">
        <div className="border border-border bg-card rounded-sm p-3"><div className="text-muted-foreground uppercase tracking-wider">Entries</div><div className="text-2xl font-display font-black text-primary" data-testid="bwso-count">{totals.count}</div></div>
        <div className="border border-border bg-card rounded-sm p-3"><div className="text-muted-foreground uppercase tracking-wider">Total Qty</div><div className="text-2xl font-display font-black" data-testid="bwso-total-qty">{totals.total_qty}</div></div>
        <div className="border border-border bg-card rounded-sm p-3"><div className="text-muted-foreground uppercase tracking-wider">Total Value</div><div className="text-2xl font-display font-black" data-testid="bwso-value">₹{totals.total_value.toLocaleString("en-IN")}</div></div>
      </div>

      <div className="border border-border bg-card rounded-sm overflow-hidden overflow-x-auto">
        <table className="w-full text-sm" data-testid="bwso-table">
          <thead className="bg-secondary text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            <tr>
              <th className="text-left p-3">Date</th>
              <th className="text-left p-3">Item Name</th>
              <th className="text-left p-3">Category</th>
              <th className="text-left p-3">Company</th>
              <th className="text-left p-3">Group</th>
              <th className="text-left p-3">Batch #</th>
              <th className="text-left p-3">Store</th>
              <th className="text-right p-3">Qty</th>
              <th className="text-left p-3">Unit</th>
              <th className="text-right p-3">MRP</th>
              <th className="text-right p-3">Purchase Rate</th>
              <th className="text-right p-3">Total Value</th>
              <th className="text-left p-3">Remarks</th>
              <th className="text-right p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={14} className="p-6 text-center text-muted-foreground" data-testid="bwso-empty">No opening entries{filtersActive ? " match the filters" : " yet — click New Opening Entry"}</td></tr>}
            {pg.pageItems.map(r => (
              <tr key={r.id} data-testid={`bwso-row-${r.id}`} className="border-t border-border hover:bg-secondary/40">
                <td className="p-3 text-xs">{r.entry_date || "—"}</td>
                <td className="p-3">{r.item_name}</td>
                <td className="p-3 text-xs text-muted-foreground">{r.category || "—"}</td>
                <td className="p-3 text-xs text-muted-foreground">{r.company || "—"}</td>
                <td className="p-3 text-xs text-muted-foreground">{r.group || "—"}</td>
                <td className="p-3 font-mono text-xs">{r.batch_number}</td>
                <td className="p-3 text-xs">{r.store_name || "—"}</td>
                <td className="p-3 text-right font-mono font-semibold">{r.qty}</td>
                <td className="p-3 text-xs text-muted-foreground">{r.unit}</td>
                <td className="p-3 text-right font-mono">₹{Number(r.mrp || 0).toLocaleString("en-IN")}</td>
                <td className="p-3 text-right font-mono">₹{Number(r.purchase_rate || 0).toLocaleString("en-IN")}</td>
                <td className="p-3 text-right font-mono">₹{Number(r.total_value || 0).toLocaleString("en-IN")}</td>
                <td className="p-3 text-xs text-muted-foreground max-w-[10rem] truncate" title={r.remarks || ""}>{r.remarks || "—"}</td>
                <td className="p-3 text-right whitespace-nowrap">
                  <Button data-testid={`bwso-edit-${r.id}`} variant="ghost" size="sm" onClick={() => openEdit(r)} className="h-7 px-2"><Pencil className="w-3 h-3"/></Button>
                  <Button data-testid={`bwso-del-${r.id}`} variant="ghost" size="sm" onClick={() => setDelRow(r)} className="h-7 px-2 text-destructive"><Trash2 className="w-3 h-3"/></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <PaginationBar pg={pg}/>
      </div>

      {serialIdx !== null && form.lines[serialIdx] && (
        <SerialDialog
          itemName={items.find(x => x.id === form.lines[serialIdx].item_id)?.item_name || `Line ${serialIdx + 1}`}
          initial={form.lines[serialIdx].serial_numbers || []}
          onClose={() => setSerialIdx(null)}
          onApply={(serials) => { applySerials(serialIdx, serials); setSerialIdx(null); }}
        />
      )}

      <Dialog open={importOpen} onOpenChange={(v) => { setImportOpen(v); if (!v) { setFile(null); setPreview(null); } }}>        <DialogContent className="rounded-sm bg-card border-border max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="bwso-import-dialog">
          <DialogHeader><DialogTitle className="font-display uppercase">Import Opening Stock</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="border border-primary/30 bg-primary/5 rounded-sm p-3 text-xs flex items-center justify-between">
              <span>Columns: item_name, batch_number, qty, mrp, purchase_rate, store, entry_date, remarks. <span className="text-primary font-semibold">Download the sample template</span> first.</span>
              <Button data-testid="bwso-download-template" size="sm" variant="ghost" onClick={getTemplate} className="text-xs text-primary"><Download className="w-3 h-3 mr-1"/> Template CSV</Button>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">CSV or Excel file (.csv, .xlsx)</Label>
              <Input data-testid="bwso-import-file" type="file" accept=".csv,.xlsx" onChange={(e) => { setFile(e.target.files[0]); setPreview(null); }} className="mt-1 rounded-sm bg-secondary border-border"/>
            </div>
            <div className="flex gap-2">
              <Button data-testid="bwso-preview-import" onClick={doPreview} disabled={!file || importing} variant="outline" className="rounded-sm text-xs uppercase tracking-wider">Preview</Button>
              <Button data-testid="bwso-confirm-import" onClick={doImport} disabled={!file || importing || (preview && preview.count === 0)} className="rounded-sm bg-primary text-primary-foreground uppercase tracking-[0.15em] text-xs">{importing ? "Working…" : "Import"}</Button>
            </div>
            {preview && (
              <div className="border border-border rounded-sm">
                <div className="p-3 border-b border-border grid grid-cols-3 gap-2 text-xs">
                  <div className="flex items-center gap-2 text-green-500"><CheckCircle2 className="w-3.5 h-3.5"/> <span className="font-mono font-semibold" data-testid="bwso-preview-count">{preview.count}</span> valid</div>
                  <div className="flex items-center gap-2 text-destructive"><AlertCircle className="w-3.5 h-3.5"/> <span className="font-mono font-semibold" data-testid="bwso-preview-errors">{preview.errors.length}</span> errors</div>
                  <div className="text-muted-foreground text-right">Total rows: {preview.total_rows}</div>
                </div>
                {preview.errors.length > 0 && (
                  <div className="p-3 max-h-40 overflow-y-auto space-y-1" data-testid="bwso-import-errors">
                    {preview.errors.slice(0, 20).map((e, i) => (
                      <div key={i} className="text-[11px] text-destructive font-mono">Row {e.row}: {e.error}</div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delRow} onOpenChange={(o) => { if (!o) setDelRow(null); }}>
        <AlertDialogContent data-testid="bwso-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle data-testid="bwso-delete-title">Delete Opening Entry?</AlertDialogTitle>
            <AlertDialogDescription data-testid="bwso-delete-message">
              This will delete batch {delRow?.batch_number} for {delRow?.item_name} and reverse its opening stock of {delRow?.qty} {delRow?.unit}. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="bwso-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="bwso-delete-confirm" onClick={doDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Delete Permanently</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
